import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import ExamCourse from "./components/learning/pages/examCourse";
import { useSidebar } from "./context/sidebarContext";
import Sidebar from "./Sidebar";
import styles from "./styles/Dashboard.module.css";
import { baseAxios } from "./apis";
import { useCsrfToken } from "./hooks/useCsrfToken";
import Layout from "./components/ui/Layout";

// 1. Create a custom hook that handles BOTH sequential API calls.
const useExamAttemptQuery = (exam_id) => {
  const { fetchCsrfToken } = useCsrfToken();

  return useQuery({
    queryKey: ["examAttempt", exam_id],
    queryFn: async () => {
      // First, get the exam details.
      const examDetailResponse = await baseAxios.get(
        `/api/courses/exams/${exam_id}/`
      );

      // Second, get a fresh CSRF token and start the attempt.
      const freshToken = await fetchCsrfToken();
      const attemptResponse = await baseAxios.post(
        `/api/courses/exams/attempts/${exam_id}/start/`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRFToken": freshToken },
        }
      );

      // Finally, combine the results and return them as a single object.
      return {
        ...examDetailResponse.data,
        attempt_id: attemptResponse.data?.attempt_id,
      };
    },
    // Only run this query if the exam_id exists.
    enabled: !!exam_id,
  });
};

export const LearnExam = () => {
  const { exam_id } = useParams();
  const navigate = useNavigate();
  const { isOpen, toggleSidebar } = useSidebar();

  // 2. Call our new hook to get the combined data and all necessary states.
  const {
    data: examDetail,
    isLoading: loading,
    isError,
  } = useExamAttemptQuery(exam_id);

  // All manual useState and data-fetching useEffect hooks are now gone.

  // This effect for redirecting if no ID is present remains.
  useEffect(() => {
    if (!exam_id) {
      navigate("/learn", { replace: true });
    }
  }, [exam_id, navigate]);

  // 3. Use a separate useEffect to handle navigation on error.
  useEffect(() => {
    if (isError) {
      console.error("Exam not found or error starting attempt.");
      navigate("/learn", { replace: true });
    }
  }, [isError, navigate]);

  // Prevent rendering if there's no ID.
  if (!exam_id) return null;

  return (
    <Layout>
      <ExamCourse examDetail={examDetail} loading={loading} />
    </Layout>
  );
};
